// =====================================================================
// src/lib/v2/firestore/connections.js
//
// V2 Firestore data layer for /connections.
//
// SCHEMA (enforced by firestore.rules):
//   - Doc ID: `${auth.uid}_${businessId}` (deterministic; rules check this)
//   - userId: string         — MUST equal auth.uid on create (rule-enforced)
//   - businessId: string     — must reference a status='verified' business
//   - status: 'active' | 'disconnected' | 'lapsed'
//   - createdAt: serverTimestamp (IMMUTABLE after creation)
//
// CRITICAL DESIGN DECISIONS (June 9 2026):
//
//   1. AUTH SOURCE: userId is derived from `auth.currentUser.uid` INTERNALLY,
//      not passed as a parameter. The Firestore rule checks `request.auth.uid`,
//      so the client must use the same value. (Earlier bug: callers passing
//      a different `currentUser.id` caused permission-denied.)
//
//   2. NO READ BEFORE WRITE: connectToBusiness() does NOT call getDoc() first
//      to check if the doc exists. Reason: the READ rule requires
//      `resource.data.userId == request.auth.uid`, but on a non-existent doc
//      `resource` is null → rule denies → permission-denied error before we
//      can even act. Instead, we try updateDoc first (works if doc exists)
//      and fall through to setDoc on failure (creates if doc doesn't exist).
//
//   3. getConnection() catches permission-denied and returns null. A non-
//      existent connection doc throws permission-denied because the READ rule
//      can't evaluate against null resource — treating that as "no connection"
//      is correct semantically AND preserves the rule's privacy guarantee
//      (we never confirm OR deny the existence of other users' connections).
//
// IMPORTANT:
//   - Hard delete is blocked by rules (`allow delete: if false`). "Remove" a
//     connection by setting status='disconnected' via updateDoc.
//
// =====================================================================

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  setDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { auth, db } from '../../firebase';

// ============================================================
// Internal helpers
// ============================================================

function connectionId(userId, businessId) {
  return `${userId}_${businessId}`;
}

function requireUid() {
  const uid = auth.currentUser?.uid;
  if (!uid) {
    throw new Error('Not signed in — connection operation requires auth');
  }
  return uid;
}

// ============================================================
// getConnection(businessId)
// ============================================================
//
// Returns the current user's connection doc for this business (any status),
// or null if not signed in / no connection exists / read denied.
//
// Note on the catch: when a connection doc doesn't exist, the READ rule
// evaluates against null resource and denies. We treat permission-denied
// as "no connection" here — it's semantically correct AND avoids leaking
// info about other users' connections.
//
export async function getConnection(businessId) {
  if (!businessId) return null;
  const uid = auth.currentUser?.uid;
  if (!uid) return null;
  try {
    const ref = doc(db, 'connections', connectionId(uid, businessId));
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  } catch (err) {
    // Non-existent doc + the rule's null-resource denial both surface as
    // permission-denied. Treat either as "no connection".
    if (err?.code === 'permission-denied') return null;
    throw err;
  }
}

// ============================================================
// getUserConnectionIds()
// ============================================================
//
// Returns a Set of businessIds the current user is connected to with
// status='active'. Empty Set if not signed in.
//
// Queries (unlike single-doc reads) don't have the null-resource problem:
// Firestore evaluates the rule against each matching doc, excludes denials
// silently. The where('userId', '==', uid) filter aligns with the rule's
// ownership check.
//
// Requires composite index: connections (userId ASC, status ASC, __name__).
// Declared in firestore.indexes.json.
//
export async function getUserConnectionIds() {
  const uid = auth.currentUser?.uid;
  if (!uid) return new Set();
  const q = query(
    collection(db, 'connections'),
    where('userId', '==', uid),
    where('status', '==', 'active'),
  );
  const snap = await getDocs(q);
  return new Set(snap.docs.map(d => d.data().businessId));
}

// ============================================================
// connectToBusiness(businessId)
// ============================================================
//
// Idempotent: works whether the user has never connected before OR
// previously disconnected and is now reconnecting.
//
// Implementation uses try-update-first to avoid the "read non-existent doc"
// problem (see file-level note 2 above):
//   1. Try updateDoc(status='active')
//      - Doc exists (reconnect path) → succeeds, createdAt preserved
//      - Doc doesn't exist → throws (permission-denied OR not-found)
//   2. On failure, fall through to setDoc with full payload
//      - This is the CREATE path; rule allows it for verified business
//
// Throws if not signed in, or on a final permission failure.
//
export async function connectToBusiness(businessId) {
  if (!businessId) throw new Error('connectToBusiness: businessId required');
  const uid = requireUid();
  const ref = doc(db, 'connections', connectionId(uid, businessId));

  // Try update path first — works if doc exists (reconnect case)
  try {
    await updateDoc(ref, { status: 'active' });
    return;
  } catch (err) {
    // Doc likely doesn't exist. Firestore returns either 'not-found' or
    // 'permission-denied' here depending on which check the rules engine
    // runs first; we treat both as "fall through to create". Any other
    // error (e.g. unavailable, network) propagates.
    const isMissing = err?.code === 'not-found'
      || err?.code === 'permission-denied';
    if (!isMissing) throw err;
  }

  // Create path — doc doesn't exist, write the full payload.
  await setDoc(ref, {
    userId: uid,
    businessId,
    status: 'active',
    createdAt: serverTimestamp(),
  });
}

// ============================================================
// getUserConnections()
// ============================================================
//
// Returns the current user's ACTIVE connection docs as a flat array,
// sorted by createdAt descending (newest first). Empty array if not
// signed in.
//
// Each element has the full connection schema:
//   { id, userId, businessId, status, createdAt }
//
// Unlike getUserConnectionIds() (which returns just a Set of business IDs
// for lightweight "is this connected?" checks), this returns full docs so
// callers can use createdAt for "connected X ago" display + sorting.
//
// Callers typically join with /businesses client-side: fetch this list,
// then call getBusiness() for each businessId to assemble display data.
//
// Uses the same composite index as getUserConnectionIds (userId + status
// + __name__). Sort happens client-side because adding orderBy(createdAt)
// would require a new composite index, and N is small per user.
//
export async function getUserConnections() {
  const uid = auth.currentUser?.uid;
  if (!uid) return [];
  const q = query(
    collection(db, 'connections'),
    where('userId', '==', uid),
    where('status', '==', 'active'),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => {
      // Firestore Timestamps → millis; null/undefined → 0 (rare; sorts last)
      const aMs = a.createdAt?.toMillis?.() ?? 0;
      const bMs = b.createdAt?.toMillis?.() ?? 0;
      return bMs - aMs;
    });
}

// ============================================================
// disconnectFromBusiness(businessId)
// ============================================================
//
// Soft delete. Sets status='disconnected'. The doc remains in
// Firestore so we keep history (createdAt, etc.) for potential
// reconnect later.
//
// Throws if not signed in, or if the doc doesn't exist
// (you can't disconnect a connection that was never made).
//
export async function disconnectFromBusiness(businessId) {
  if (!businessId) throw new Error('disconnectFromBusiness: businessId required');
  const uid = requireUid();
  const ref = doc(db, 'connections', connectionId(uid, businessId));
  await updateDoc(ref, { status: 'disconnected' });
}
